import {
    useGetCascadesExportMutation,
    useGetCascadesGraphQuery,
    useGetCascadesReportQuery,
} from "api/TenantSlug/Report/hooks";
import { useReport } from "components/Graph/hooks/useReport.ts";
import { useReportRowSelectors } from "components/Graph/hooks/useReportSelectors.ts";
import { EntityGraph } from "components/Graph/UI/EntityGraph/EntityGraph.tsx";
import { ShowInGraphTableButton } from "components/Graph/UI/Miscs/ShowInGraphTableBtn.tsx";
import { GeneralGraphUtils } from "components/Graph/utils/GeneralGraphUtils.ts";
import { ALLOWED_KEYS, DEFAULT_VISIBLE_KEYS } from "components/Report/PingTreeReport/config.ts";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { FILTER_TYPES } from "components/UI/Table/constants/filter.ts";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { MoneyCell } from "components/UI/Table/UI/MoneyCell/MoneyCell.tsx";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { generateSummary } from "components/UI/Table/utils/summary.tsx";
import { TenantLink } from "components/UI/TenantLink/TenantLink.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions.ts";
import { Fragment } from "react";
import { useRecoilValue } from "recoil";
import { PermissionEnum } from "types/enums.ts";
import { TableId } from "types/models";

import GetCascadeReportResponse = App.Http.Responses.Report.GetCascadeReportResponse;
import CascadeReportSeriesResponse = App.Http.Responses.Report.CascadeReportSeriesResponse;
import GetCascadeReportExportRequest = App.Http.Requests.Report.GetCascadeReportExportRequest;

interface PingTreeReportProps {
    userId?: string;
}

export const PingTreeReport = ({ userId }: PingTreeReportProps) => {
    const tableId = TableId.PingTreeReportTable;
    const tableAtomInstance = tableAtom<CascadeReportSeriesResponse>();
    const { t } = usePaldockTranslation("common");
    const { t: tR } = usePaldockTranslation("ui.report.columns");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const permissionsUtils = usePermission();
    const exportState = useRecoilValue(exportAtom(tableId));
    const createdAtStr = useCreatedAtFilterValue();
    const { page, filter } = useRecoilValue(tableAtomInstance(tableId));

    const { sortBy, setSortBy } = useReport("cascade-graph", "total", TableId.PingTreeReportTable);

    const {
        data: graphData,
        isLoading: graphLoading,
        isError: graphError,
    } = useGetCascadesGraphQuery({
        filter: {
            filter: {
                ...(userId && { user_id: userId }),
                ...(filter?.filter || {}),
                ...(filter?.filter?.date_at
                    ? {}
                    : {
                          date_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            include: "offer",
        },
    });

    const request = {
        page: {
            number: page?.number ?? DEFAULT_PAGE_NUMBER,
            size: page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                ...(userId && { user_id: userId }),
                ...(filter?.filter || {}),
                ...(filter?.filter?.date_at
                    ? {}
                    : {
                          date_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            include: "offer",
            sort: filter?.sort ?? `-${sortBy}`,
        },
    };

    const query = useGetCascadesReportQuery(request, {
        enabled: false,
    });

    const getCascadesExportMutation = useGetCascadesExportMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetCascadeReportResponse,
        unknown,
        Record<string, unknown>,
        GetCascadeReportExportRequest
    >(tableId, query, undefined, undefined, getCascadesExportMutation);

    const columnConfig: TableColumnType<CascadeReportSeriesResponse>[] = [
        {
            title: tG("title"),
            dataIndex: "title",
            key: "title",
            filterable: true,
            filterType: FILTER_TYPES.string,
            ellipsis: true,
            render: (title, record) => (
                <TenantLink to={`/report/pingtree/${record.offer?.id}`} className="underline text-blue-600">
                    {title}
                </TenantLink>
            ),
            sorter: true,
            width: 150,
        },
        {
            title: t("total"),
            dataIndex: "total",
            key: "total",
            render: (total) => total ?? 0,
            filterable: true,
            filterType: FILTER_TYPES.number,
            ellipsis: true,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            width: 120,
        },
        {
            title: tR("filtered"),
            dataIndex: "filtered",
            key: "filtered",
            render: (filtered) => filtered ?? 0,
            filterable: true,
            filterType: FILTER_TYPES.number,
            ellipsis: true,
            sorter: true,
            width: 120,
        },
        {
            title: tR("error_check"),
            dataIndex: "error_check",
            key: "error_check",
            render: (error_check) => error_check ?? 0,
            filterable: true,
            filterType: FILTER_TYPES.number,
            ellipsis: true,
            sorter: true,
            width: 120,
        },
        {
            title: tR("refused_check"),
            dataIndex: "refused_check",
            key: "refused_check",
            render: (refused_check) => refused_check ?? 0,
            filterable: true,
            filterType: FILTER_TYPES.number,
            ellipsis: true,
            sorter: true,
            width: 120,
        },
        {
            title: tR("checked"),
            dataIndex: "checked",
            key: "checked",
            render: (checked) => checked ?? 0,
            filterable: true,
            filterType: FILTER_TYPES.number,
            ellipsis: true,
            sorter: true,
            width: 120,
        },
        /* {
             title: tR("report.shared.verify"),
             dataIndex: "verify",
             key: "verify",
             render: (verify) => verify ?? 0,
             filterable: true,
             filterType: FILTER_TYPES.number,
             sorter: true,
         },
         {
             title: tR("report.shared.error_verify"),
             dataIndex: "error_verify",
             key: "error_verify",
             render: (error_verify) => error_verify ?? 0,
             filterable: true,
             filterType: FILTER_TYPES.number,
             sorter: true,
         },
         {
             title: tR("report.shared.refused_verify"),
             dataIndex: "refused_verify",
             key: "refused_verify",
             render: (refused_verify) => refused_verify ?? 0,
             filterable: true,
             filterType: FILTER_TYPES.number,
             sorter: true,
         },*/
        /*{
            title: tR("report.shared.verified"),
            dataIndex: "verified",
            key: "verified",
            render: (verified) => verified ?? 0,
            filterable: true,
            filterType: FILTER_TYPES.number,
            sorter: true,
        },*/
        {
            title: tR("error_send"),
            dataIndex: "error_send",
            key: "error_send",
            render: (error_send) => error_send ?? 0,
            filterable: true,
            filterType: FILTER_TYPES.number,
            ellipsis: true,
            sorter: true,
            width: 120,
        },
        {
            title: tR("refused_send"),
            dataIndex: "refused_send",
            key: "refused_send",
            render: (refused_send) => refused_send ?? 0,
            filterable: true,
            filterType: FILTER_TYPES.number,
            ellipsis: true,
            sorter: true,
            width: 120,
        },
        {
            title: tR("send"),
            dataIndex: "send",
            key: "send",
            render: (send) => send ?? 0,
            filterable: true,
            filterType: FILTER_TYPES.number,
            ellipsis: true,
            sorter: true,
            width: 120,
        },
        {
            title: tR("accepted"),
            dataIndex: "accepted",
            key: "accepted",
            render: (accepted) => accepted ?? 0,
            filterable: true,
            filterType: FILTER_TYPES.number,
            ellipsis: true,
            sorter: true,
            width: 120,
        },
        {
            title: tR("redirected"),
            dataIndex: "redirected",
            key: "redirected",
            render: (redirected) => redirected ?? 0,
            filterable: true,
            filterType: FILTER_TYPES.number,
            ellipsis: true,
            sorter: true,
            width: 120,
        },
        /*  {
              title: tR("report.shared.approved"),
              dataIndex: "approved",
              key: "approved",
              render: (approved) => approved ?? 0,
              filterable: true,
              filterType: FILTER_TYPES.number,
              sorter: true,
          },*/
        {
            title: tR("rejected"),
            dataIndex: "rejected",
            key: "rejected",
            render: (rejected) => rejected ?? 0,
            filterable: true,
            filterType: FILTER_TYPES.number,
            ellipsis: true,
            sorter: true,
            width: 120,
        },
        {
            title: tR("epl"),
            dataIndex: "epl",
            key: "epl",
            render: (_, record) => <MoneyCell money={record.epl} />,
            filterable: true,
            filterType: FILTER_TYPES.number,
            ellipsis: true,
            sorter: true,
            width: 130,
        },
        {
            title: tR("revenue"),
            dataIndex: "revenue",
            key: "revenue",
            render: (_, record) => <MoneyCell money={record.revenue} />,
            filterable: true,
            filterType: FILTER_TYPES.number,
            ellipsis: true,
            sorter: true,
            width: 130,
        },
    ];

    const summaryKeys = [
        "total",
        "filtered",
        "error_check",
        "refused_check",
        "checked",
        //"verify",
        //"error_verify",
        //"refused_verify",
        //"verified",
        "error_send",
        "refused_send",
        "send",
        "accepted",
        "redirected",
        //"approved",
        "rejected",
        "epl",
        "revenue",
    ];

    const { selectedRows, isSelectionOn, setIsSelectionOn, defaultSelection } = useReportRowSelectors(
        Object.values(graphData?.timeline || {}).flatMap((item) => Object.values(item)),
        (record) => record?.offer?.id,
        sortBy
    );

    const data = GeneralGraphUtils.prepareEntityData(graphData?.timeline);

    return (
        <Fragment>
            {permissionsUtils.hasPermission(PermissionEnum.ReportShowInGraph) && (
                <EntityGraph
                    data={data}
                    entity={"offer"}
                    entityId={"offer.id"}
                    keys={ALLOWED_KEYS}
                    graphId={"cascade-graph"}
                    defaultActiveKey={"total"}
                    defaultVisibleKeys={DEFAULT_VISIBLE_KEYS}
                    loading={graphLoading}
                    error={graphError}
                    onActiveHighlightChange={(key) => setSortBy(key)}
                    isSelectionOn={isSelectionOn}
                    defaultSelectedEntities={defaultSelection}
                    selectedEntities={selectedRows}
                    summaryData={tableFetchDataUtils.tableData?.summary}
                />
            )}
            <div className={"mt-4"}>
                <Table<GetCascadeReportResponse, CascadeReportSeriesResponse>
                    tableId={tableId}
                    data={tableFetchDataUtils.tableData as GetCascadeReportResponse}
                    rowSelection={undefined}
                    dataSource={tableFetchDataUtils.tableData?.series || []}
                    columnConfig={columnConfig}
                    loading={tableFetchDataUtils.fetchQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                    onRefresh={tableFetchDataUtils.refetchData}
                    showRefetchButton={permissionsUtils.hasPermission(PermissionEnum.ReportUpdate)}
                    enableFilters={permissionsUtils.hasPermission(PermissionEnum.ReportSelectFilter)}
                    enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.ReportSelectFilterTime)}
                    summary={(visibleColumns) =>
                        generateSummary(
                            tableFetchDataUtils.tableData?.summary,
                            summaryKeys,
                            [],
                            t("total"),
                            visibleColumns ?? undefined,
                            columnConfig,
                            false
                        )
                    }
                    rowKey={(record, index) => record.offer?.id || `fallback-${index}`}
                    autoScrollWidth={true}
                    onExport={() =>
                        tableFetchDataUtils.exportData({
                            extension: exportState.extension,
                            headers: exportState.headers,
                            export_all: exportState.range === "all",
                            page: request.page,
                            filter: request.filter,
                            delimiter: exportState.delimiter,
                        })
                    }
                    dateFilterDataIndex={"date_at"}
                    extraTopBarIcons={[
                        <ShowInGraphTableButton
                            key="show-in-graph"
                            isOn={isSelectionOn}
                            onIsOnChange={setIsSelectionOn}
                        />,
                    ]}
                />
            </div>
        </Fragment>
    );
};
